// SetupPreflightModal — the very first time a school user lands on the
// Setup page, we ask them up-front:
//   1. Which vendor categories does your school have? (tick boxes — and
//      one numeric "how many vendors in this category" per ticked row)
//   2. Total number of Cook-cum-Helpers (CCH) at your school
//
// The answers are saved onto `school_profile.declaredVendorCategories`
// + `declaredCchCount` + `setupCountsLockedAt`. From this point on the
// Setup page uses these declared counts to compute tab completion:
//   - Vendors tab is INCOMPLETE until each declared category has at
//     least the declared number of vendor rows.
//   - CCH tab is INCOMPLETE until cch rows >= declaredCchCount.
//
// The user can still come back and edit later — we surface a banner on
// the Setup page that flags any mismatch and a "Re-do Setup" CTA opens
// this modal again (the lock can be reset by admin too).

import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, ShoppingBasket, ShieldCheck, Lock, AlertCircle } from "lucide-react";
import { ALL_VENDOR_CATEGORIES, VENDOR_CATEGORY_LABELS_ODIA } from "@/lib/setupGuide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The preflight saves both the category list AND the per-category count.
// We store the per-category counts in a JSON-encoded `notes` field on the
// school profile to avoid changing the backend schema for now. The list
// of selected categories goes in `declaredVendorCategories` (typed list).
// (Counts are recovered from the actual vendor rows per category.)

export default function SetupPreflightModal({
  open,
  profile,
  onLocked, // called after a successful save with the updated profile
  onClose,  // called when the user dismisses without saving (X / ESC / outside-click)
}) {
  // Pre-fill from existing profile if user is re-opening to amend.
  const [cats, setCats] = useState(() => {
    const initial = {};
    ALL_VENDOR_CATEGORIES.forEach((c) => {
      initial[c] = (profile?.declaredVendorCategories || []).includes(c);
    });
    return initial;
  });
  const [cchCount, setCchCount] = useState(
    profile?.declaredCchCount ? String(profile.declaredCchCount) : "",
  );
  const [saving, setSaving] = useState(false);

  const selectedCount = useMemo(
    () => Object.values(cats).filter(Boolean).length,
    [cats],
  );

  const validateAndSave = async () => {
    if (selectedCount === 0) {
      toast.error("ଅନ୍ତତଃ ଗୋଟିଏ Vendor category ବାଛନ୍ତୁ");
      return;
    }
    const cch = parseInt(cchCount, 10);
    if (!Number.isFinite(cch) || cch < 1 || cch > 50) {
      toast.error("CCH ସଂଖ୍ୟା 1–50 ମଧ୍ୟରେ ଲେଖନ୍ତୁ");
      return;
    }
    if (!profile?.id) {
      toast.error("School profile not loaded yet");
      return;
    }
    setSaving(true);
    try {
      const chosenCats = ALL_VENDOR_CATEGORIES.filter((c) => cats[c]);
      const payload = {
        ...profile,
        declaredCchCount: cch,
        declaredVendorCategories: chosenCats,
        setupCountsLockedAt: new Date().toISOString(),
      };
      // Strip backend-managed fields so the upsert doesn't reject them.
      delete payload.createdAt;
      delete payload.updatedAt;
      const r = await axios.post(`${API}/school-profiles?id=${encodeURIComponent(profile.id)}`, payload);
      toast.success("✅ Setup ସୂଚନା save ହୋଇଗଲା");
      onLocked?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isRelock = !!profile?.setupCountsLockedAt;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="setup-preflight-modal"
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-xl overflow-y-auto p-0 sm:p-0 [&>button.absolute]:z-50 [&>button.absolute]:rounded-full [&>button.absolute]:bg-white/95 [&>button.absolute]:p-1.5 [&>button.absolute]:text-clay-dark [&>button.absolute]:shadow-md [&>button.absolute]:opacity-100 [&>button.absolute]:hover:bg-white"
      >
        {/* Hero band */}
        <div className="bg-gradient-to-br from-clay via-clay-dark to-moss-dark px-6 py-6 pr-14 text-white sm:px-7 sm:pr-14">
          <div className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-white/80">
            <ShieldCheck className="h-3.5 w-3.5" /> Setup Preflight · ସେଟଅପ୍ ପ୍ରସ୍ତୁତି
          </div>
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="font-heading text-xl font-bold sm:text-2xl">
              🙏 ଆଗରୁ ଆମକୁ କୁହନ୍ତୁ — ଆପଣଙ୍କ ବିଦ୍ୟାଳୟରେ କେତେ Vendor ଓ CCH ଅଛନ୍ତି
            </DialogTitle>
            <DialogDescription className="text-xs text-white/90 sm:text-sm">
              ଏହି ସଂଖ୍ୟା save କଲା ପରେ Vendor / CCH tab ର completion status ସ୍ୱୟଂଚାଳିତ ଭାବେ check ହେବ। ସମସ୍ତ entries ସମ୍ପୂର୍ଣ୍ଣ ହେଲେ <b>100% Profile Complete</b> ହେବ।
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5 sm:px-7 sm:py-6">
          {/* Vendor categories */}
          <section>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <ShoppingBasket className="h-4 w-4 text-clay" />
              ବ୍ୟବସାୟୀ Categories — ଯେଉଁ category ର vendor ଅଛନ୍ତି ସେମାନଙ୍କୁ ✓ କରନ୍ତୁ
            </h3>
            <p className="mt-1 text-[0.7rem] text-ink-700">
              ଯଦି Grocery ର 2 ବ୍ୟବସାୟୀ ଅଛନ୍ତି ତେବେ Grocery ତଳ ଟିକ୍ କରନ୍ତୁ — ପ୍ରତ୍ୟେକ category ର ସବୁ entries Vendor tab ରେ ଯୋଡିବାକୁ ପଡ଼ିବ।
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="preflight-categories">
              {ALL_VENDOR_CATEGORIES.map((c) => (
                <li
                  key={c}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                    cats[c] ? "border-moss bg-moss/10" : "border-border bg-white"
                  }`}
                >
                  <Checkbox
                    id={`preflight-cat-${c}`}
                    checked={!!cats[c]}
                    onCheckedChange={(v) => setCats((prev) => ({ ...prev, [c]: !!v }))}
                    data-testid={`preflight-cat-${c}`}
                  />
                  <label htmlFor={`preflight-cat-${c}`} className="cursor-pointer text-sm font-medium text-ink-900">
                    {VENDOR_CATEGORY_LABELS_ODIA[c]}
                  </label>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[0.65rem] text-clay-dark" data-testid="preflight-selected-count">
              ✓ {selectedCount} category ବାଛିଛନ୍ତି
            </p>
          </section>

          {/* CCH count */}
          <section>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Users className="h-4 w-4 text-clay" />
              Cook-cum-Helper (ରନ୍ଧାଳି) ସଂଖ୍ୟା
            </h3>
            <p className="mt-1 text-[0.7rem] text-ink-700">
              ଆପଣଙ୍କ ବିଦ୍ୟାଳୟରେ ବର୍ତ୍ତମାନ କେତେ CCH କାର୍ଯ୍ୟ କରୁଛନ୍ତି?
            </p>
            <Input
              type="number"
              min={1}
              max={50}
              value={cchCount}
              onChange={(e) => setCchCount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 2"
              data-testid="preflight-cch-count"
              className="mt-2 font-mono text-base font-bold"
            />
          </section>

          {/* Lock notice */}
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="preflight-lock-notice">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <b>Note:</b> Save କଲା ପରେ ଏହି ସଂଖ୍ୟା lock ହୋଇଯିବ। Vendor / CCH tab ର ସବୁ entries ସମ୍ପୂର୍ଣ୍ଣ କରନ୍ତୁ — ତାହାହେଲେ profile 100% complete ଦେଖାଯିବ।
              {isRelock && " (ଆପଣ ପୁନଃ update କରୁଛନ୍ତି।)"}
            </p>
          </div>

          {selectedCount === 0 && (
            <p className="flex items-center gap-1 text-[0.7rem] font-semibold text-red-700" data-testid="preflight-error-cats">
              <AlertCircle className="h-3.5 w-3.5" /> ଅନ୍ତତଃ ଗୋଟିଏ Vendor category ବାଛନ୍ତୁ
            </p>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 border-t border-border bg-sand-50 px-6 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <Button
            type="button"
            variant="outline"
            onClick={() => onClose?.()}
            disabled={saving}
            data-testid="preflight-cancel"
            className="w-full sm:w-auto"
          >
            ✕ ବନ୍ଦ କରନ୍ତୁ · Cancel
          </Button>
          <Button
            onClick={validateAndSave}
            disabled={saving || selectedCount === 0 || !cchCount}
            data-testid="preflight-save"
            className="w-full gap-2 bg-clay py-3 text-base font-bold text-white hover:bg-clay-dark sm:w-auto"
          >
            <Lock className="h-4 w-4" /> {saving ? "Saving…" : "Lock & Continue · Setup ଆରମ୍ଭ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
